import { useState } from 'react';
import { Users, UserPlus, RefreshCw, Edit, UserX } from 'lucide-react';
import { useOrgUsers, useInviteUser, useUpdateUser, useDeactivateUser, useResendInvite } from '../../hooks/useApi';
import type { OrgUser } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';
import type { UserRole } from '../../types/auth';

const ROLE_LABELS: Record<string, string> = {
  viewer: 'Viewer',
  recruiter: 'Recruiter',
  admin: 'Admin',
  super_admin: 'Super Admin',
};

const ROLE_BADGE_CLASSES: Record<string, string> = {
  admin: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  recruiter: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  viewer: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  super_admin: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
};

const INVITEABLE_ROLES: UserRole[] = ['viewer', 'recruiter', 'admin'];

export function TeamSettings() {
  const { showToast } = useToast();
  const { user: currentUser, isAdmin } = useAuth();
  const { data: members = [], isLoading } = useOrgUsers();
  const inviteUser = useInviteUser();
  const updateUser = useUpdateUser();
  const deactivateUser = useDeactivateUser();
  const resendInvite = useResendInvite();

  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<UserRole>('viewer');

  const [editDialogUser, setEditDialogUser] = useState<OrgUser | null>(null);
  const [editRole, setEditRole] = useState<UserRole>('viewer');

  const [deactivateTarget, setDeactivateTarget] = useState<OrgUser | null>(null);

  const handleInviteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await inviteUser.mutateAsync({ email: inviteEmail.trim(), role: inviteRole });
      showToast('Invitation sent', 'success');
      setInviteDialogOpen(false);
      setInviteEmail('');
      setInviteRole('viewer');
    } catch {
      showToast('Failed to send invitation', 'error');
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editDialogUser) return;
    try {
      await updateUser.mutateAsync({ id: editDialogUser.id, role: editRole });
      showToast('User updated', 'success');
      setEditDialogUser(null);
    } catch {
      showToast('Failed to update user', 'error');
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    try {
      await deactivateUser.mutateAsync(deactivateTarget.id);
      showToast('User deactivated', 'success');
      setDeactivateTarget(null);
    } catch {
      showToast('Failed to deactivate user', 'error');
    }
  };

  const handleResendInvite = async (member: OrgUser) => {
    try {
      await resendInvite.mutateAsync(member.id);
      showToast('Invitation resent', 'success');
    } catch {
      showToast('Failed to resend invitation', 'error');
    }
  };

  if (isLoading) {
    return <div className="py-8 text-center text-sm text-gray-500">Loading team members…</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">Team Members</h3>
        </div>
        {isAdmin && (
          <button
            onClick={() => setInviteDialogOpen(true)}
            className="btn btn-primary text-sm"
          >
            <UserPlus className="mr-1.5 h-4 w-4" />
            Invite Member
          </button>
        )}
      </div>

      {/* Member table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Email</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Role</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Status</th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900">
            {members.map((member) => {
              const isSelf = member.id === currentUser?.id;
              return (
                <tr key={member.id}>
                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">{member.name}</td>
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{member.email}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${ROLE_BADGE_CLASSES[member.role] ?? ROLE_BADGE_CLASSES.viewer}`}>
                      {ROLE_LABELS[member.role] ?? member.role}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {member.isActive ? (
                      <span className="inline-flex rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-300">Active</span>
                    ) : (
                      <span className="inline-flex rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300">Inactive</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      {!member.isActive && (
                        <button
                          onClick={() => handleResendInvite(member)}
                          className="btn btn-secondary text-xs"
                          disabled={resendInvite.isPending}
                        >
                          <RefreshCw className="mr-1 h-3 w-3" />
                          Resend Invite
                        </button>
                      )}
                      {isAdmin && !isSelf && (
                        <button
                          onClick={() => { setEditDialogUser(member); setEditRole(member.role as UserRole); }}
                          className="btn btn-secondary text-xs"
                        >
                          <Edit className="mr-1 h-3 w-3" />
                          Edit
                        </button>
                      )}
                      {isAdmin && !isSelf && member.isActive && (
                        <button
                          onClick={() => setDeactivateTarget(member)}
                          className="btn btn-secondary text-xs text-red-600 hover:text-red-700"
                        >
                          <UserX className="mr-1 h-3 w-3" />
                          Deactivate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Invite dialog */}
      {inviteDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" role="dialog" aria-modal="true">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">Invite Member</h2>
            <form onSubmit={handleInviteSubmit} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
                <input
                  type="email"
                  required
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                  placeholder="member@company.com"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Role</label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as UserRole)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                >
                  {INVITEABLE_ROLES.map((r) => (
                    <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setInviteDialogOpen(false)} className="btn btn-secondary text-sm">Cancel</button>
                <button type="submit" disabled={inviteUser.isPending} className="btn btn-primary text-sm">
                  {inviteUser.isPending ? 'Sending…' : 'Send Invite'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit dialog */}
      {editDialogUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" role="dialog" aria-modal="true">
          <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">Edit {editDialogUser.name}</h2>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Role</label>
                <select
                  value={editRole}
                  onChange={(e) => setEditRole(e.target.value as UserRole)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                >
                  {INVITEABLE_ROLES.map((r) => (
                    <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setEditDialogUser(null)} className="btn btn-secondary text-sm">Cancel</button>
                <button type="submit" disabled={updateUser.isPending} className="btn btn-primary text-sm">
                  {updateUser.isPending ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Deactivate confirm dialog */}
      {deactivateTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" role="dialog" aria-modal="true">
          <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
            <h2 className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100">Deactivate {deactivateTarget.name}?</h2>
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">This will prevent them from logging in. You can reactivate them later.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeactivateTarget(null)} className="btn btn-secondary text-sm">Cancel</button>
              <button
                onClick={handleDeactivate}
                disabled={deactivateUser.isPending}
                className="btn btn-primary text-sm bg-red-600 hover:bg-red-700"
              >
                {deactivateUser.isPending ? 'Deactivating…' : 'Deactivate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
